import React, { useState, useMemo, useEffect } from 'react';
import { Table, Avatar, Empty, Select, Tag, Tooltip, Row, Col, Button, Input, Modal, Form, TimePicker, Radio, message, Descriptions, Spin, Popconfirm, Pagination, Alert } from 'antd';
import {
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  StarOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  FileTextOutlined,
  FilterOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  AuditOutlined,
  EnvironmentOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';
import dayjs from 'dayjs';
import { attendanceApi, companyAddressesApi, show422Error, ValidationError } from '../../../utils/api';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { canMutate } from '../../../utils/permissions';
import { AttendanceSummaryStrip, extractAttendanceSummary } from './AttendanceSummaryMetrics';
import AttendanceAdjustedTime from './AttendanceAdjustedTime';
import AttendanceAdjustModal from './AttendanceAdjustModal';
import {
  getAttendancePalette,
  resolveAttendanceVisualKey,
  resolveIsLate,
} from '../../../utils/attendanceStatusStyles';

moment.loadPersian({ usePersianDigits: false, dialect: 'persian-modern' });

const { Option } = Select;

const EMPTY_EMPLOYEES = [];
const EMPTY_ATTENDANCE = {};

const resolveUserId = (emp) => emp?.user_id ?? emp?.userId ?? emp?.id;

const AttendanceListView = ({
  data = {},
  loading = false,
  filters = {},
  onFiltersChange,
  onRefresh,
  departments = [],
  designations = [],
  employees = [],
  defaultEmployeeId = null,
}) => {
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [form] = Form.useForm();

  // --- مودال ثبت حضور ---
  const [markModalVisible, setMarkModalVisible] = useState(false);
  const [markModalData, setMarkModalData] = useState(null); // { empId, empName, day, date }
  const [markLoading, setMarkLoading] = useState(false);

  // --- مودال مشاهده حضور ثبت‌شده ---
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailContext, setDetailContext] = useState(null);
  const [detailPermissions, setDetailPermissions] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [summaryByUserId, setSummaryByUserId] = useState({});
  const [summariesLoading, setSummariesLoading] = useState(false);

  const navigate = useNavigate();
  const { isSuperAdmin, isAdmin, user: currentUser } = usePermissions();
  const canEditDetail = isSuperAdmin || isAdmin || canMutate(detailPermissions?.edit);
  const canDeleteDetail = isSuperAdmin || isAdmin || canMutate(detailPermissions?.delete);

  const jYear = filters.year || moment().format('jYYYY');
  const jMonthName = filters.monthName || 'آذر';
  const department = filters.department || 'all';
  const designation = filters.designation || 'all';
  const employee = filters.employee || filters.userId || 'all';

  const listPayload = Array.isArray(data) ? {} : (data || {});
  const rawEmployees = listPayload.employees || EMPTY_EMPLOYEES;
  const rawAttendance = listPayload.attendance || EMPTY_ATTENDANCE;
  const rawSummaries = listPayload.summaries || listPayload.summary_by_user || null;
  const meta = listPayload.meta || {};
  const daysInMonth = meta.daysInMonth || 30;

  const filteredEmployees = useMemo(() => {
    return rawEmployees.filter((emp) => {
      const uId = resolveUserId(emp);
      const eId = emp.id;
      const detailEmpId = emp.employee_detail?.employee_id;
      const detailId = emp.employee_detail?.id;

      if (employee && employee !== 'all') {
        const target = String(employee);
        const matches =
          (eId != null && String(eId) === target) ||
          (uId != null && String(uId) === target) ||
          (detailEmpId != null && String(detailEmpId) === target) ||
          (detailId != null && String(detailId) === target);
        if (!matches) return false;
      }

      if (department && department !== 'all') {
        const deptId = emp.employee_detail?.department_id ?? emp.department_id ?? emp.team_id;
        if (deptId != null && String(deptId) !== String(department)) return false;
      }

      if (designation && designation !== 'all') {
        const desId = emp.employee_detail?.designation_id ?? emp.designation_id;
        if (desId != null && String(desId) !== String(designation)) return false;
      }

      return true;
    });
  }, [rawEmployees, employee, department, designation]);

  // بارگذاری لوکیشن‌ها هنگام باز شدن مودال
  useEffect(() => {
    if (markModalVisible && !locationsLoaded) {
      companyAddressesApi.getAll().then((res) => {
        setLocations(res || []);
        setLocationsLoaded(true);
      }).catch(() => {});
    }
  }, [markModalVisible, locationsLoaded]);

  // محاسبه شماره ماه از نام فارسی
  const jMonthNumber = useMemo(() => {
    const map = {
      'فروردین': '01', 'اردیبهشت': '02', 'خرداد': '03', 'تیر': '04',
      'مرداد': '05', 'شهریور': '06', 'مهر': '07', 'آبان': '08',
      'آذر': '09', 'دی': '10', 'بهمن': '11', 'اسفند': '12',
    };
    return map[jMonthName] || filters.month || '01';
  }, [jMonthName, filters.month]);

  const employeeIdsKey = useMemo(
    () => filteredEmployees.map((e) => resolveUserId(e)).filter((id) => id != null).join(','),
    [filteredEmployees],
  );

  // بارگذاری خلاصه ساعات ماهانه برای هر کارمند ردیف
  useEffect(() => {
    let cancelled = false;

    if (!employeeIdsKey) {
      setSummaryByUserId((prev) => (Object.keys(prev).length ? {} : prev));
      setSummariesLoading(false);
      return undefined;
    }

    const fromEmployees = {};
    filteredEmployees.forEach((emp) => {
      const uid = String(resolveUserId(emp));
      const s = extractAttendanceSummary(emp.summary)
        || extractAttendanceSummary(emp.attendance_summary)
        || extractAttendanceSummary(emp);
      if (s) fromEmployees[uid] = s;
    });
    if (rawSummaries && typeof rawSummaries === 'object') {
      Object.entries(rawSummaries).forEach(([id, s]) => {
        const extracted = extractAttendanceSummary(s);
        if (extracted) fromEmployees[String(id)] = extracted;
      });
    }

    const missingIds = employeeIdsKey
      .split(',')
      .filter(Boolean)
      .filter((id) => !fromEmployees[id]);

    if (Object.keys(fromEmployees).length) {
      setSummaryByUserId((prev) => {
        let changed = false;
        const next = { ...prev };
        Object.entries(fromEmployees).forEach(([id, summary]) => {
          if (next[id] !== summary) {
            next[id] = summary;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }

    if (!missingIds.length || !jYear || !jMonthNumber) {
      setSummariesLoading(false);
      return undefined;
    }

    setSummariesLoading(true);

    const fetchOneSummary = async (id) => {
      try {
        const res = await attendanceApi.getSummaryMetrics({
          userId: id,
          year: jYear,
          month: jMonthNumber,
        });
        const extracted = extractAttendanceSummary(res);
        if (extracted) return extracted;
      } catch (err) {
        console.warn('summary-metrics failed, trying by-member', id, err?.message);
      }
      try {
        const res = await attendanceApi.getByMember({
          userId: id,
          year: jYear,
          month: jMonthNumber,
        });
        return extractAttendanceSummary(res);
      } catch (err) {
        console.warn('by-member summary failed', id, err?.message);
        return null;
      }
    };

    (async () => {
      const results = await Promise.all(
        missingIds.map(async (id) => [String(id), await fetchOneSummary(id)]),
      );
      if (cancelled) return;
      setSummaryByUserId((prev) => {
        let changed = false;
        const next = { ...prev };
        results.forEach(([id, summary]) => {
          if (summary && next[id] !== summary) {
            next[id] = summary;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
      setSummariesLoading(false);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeIdsKey, rawSummaries, jYear, jMonthNumber]);

  const getWeekdayName = (jalaliDate) => {
    const m = moment(jalaliDate, 'jYYYY/jMM/jDD');
    if (!m.isValid()) return '';
    const dayNames = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه', 'شنبه'];
    return dayNames[m.day()];
  };

  // ---- استایل وضعیت‌ها (پالت استاندارد راهنمای تأخیر) ----
  const statusIcons = {
    present: <CheckCircleOutlined />,
    late: <ClockCircleOutlined />,
    absent: <CloseCircleOutlined />,
    leave: <FileTextOutlined />,
    half_day_leave: <FileTextOutlined />,
    half_day: <ExclamationCircleOutlined />,
    holiday: <CalendarOutlined />,
    day_off: <StopOutlined />,
    note: <StarOutlined />,
    mission: <EnvironmentOutlined />,
  };

  const getCellInfo = (val) => {
    if (!val) return { status: null, attendanceId: null, isAdjusted: false, isLate: false, visualKey: null };
    if (typeof val === 'string') {
      const visualKey = resolveAttendanceVisualKey(val);
      return { status: val, attendanceId: null, isAdjusted: false, isLate: visualKey === 'late', visualKey };
    }
    const nested = val.attendance?.[0] || val;
    const isLate = resolveIsLate(val) || resolveIsLate(nested);
    const visualKey = resolveAttendanceVisualKey({ ...nested, ...val, is_late: isLate || val.is_late || nested.is_late });
    return {
      status: val.status,
      attendanceId:
        val.id
        ?? val.attendance_id
        ?? nested?.id
        ?? null,
      isAdjusted: Boolean(
        val.is_adjusted ?? nested?.is_adjusted,
      ),
      isLate,
      visualKey,
    };
  };

  const isRecordedStatus = (status) => {
    if (!status) return false;
    const key = String(status).toLowerCase().replace(/_/g, '');
    return ['present', 'late', 'halfday', 'halfdayleave'].includes(key);
  };

  const hasRecordedAttendance = (val) => {
    const { status, attendanceId, isLate } = getCellInfo(val);
    // present با is_late هم رکورد واقعی است
    return Boolean(attendanceId) && (isRecordedStatus(status) || isLate);
  };

  const openMarkModal = (record, day, prefill = {}) => {
    const dayStr = String(day).padStart(2, '0');
    const jalaliDate = `${jYear}/${jMonthNumber}/${dayStr}`;

    setMarkModalData({
      empId: record.id,
      empName: record.name,
      designation: record.designation,
      day,
      jalaliDate,
    });

    form.resetFields();
    form.setFieldsValue({
      entryTime: prefill.entryTime ?? dayjs('08:00', 'HH:mm'),
      exitTime: prefill.exitTime ?? dayjs('16:00', 'HH:mm'),
      entryWorkFrom: 'office',
      exitWorkFrom: 'office',
      delay: 'no',
      halfDay: 'no',
      halfDayType: 'first_half',
      overwrite: false,
    });

    setMarkModalVisible(true);
  };

  const openAdjustModal = (ctx, attendance) => {
    const clockIn =
      attendance?.clock_in_time_formatted ||
      attendance?.start_time ||
      attendance?.clock_in_time ||
      null;
    const clockOut =
      attendance?.clock_out_time_formatted ||
      attendance?.end_time ||
      attendance?.clock_out_time ||
      null;
    const toHm = (v) => {
      if (!v) return null;
      if (typeof v === 'string' && /^\d{1,2}:\d{2}/.test(v)) return v.slice(0, 5);
      return moment(v).isValid() ? moment(v).format('HH:mm') : String(v).slice(0, 5);
    };
    setAdjustTarget({
      attendanceId: ctx.attendanceId || attendance?.id,
      empName: ctx.empName,
      jalaliDate: ctx.jalaliDate,
      clockIn: toHm(clockIn),
      clockOut: toHm(clockOut),
    });
    setAdjustModalOpen(true);
  };

  const handleViewAttendance = async (record, day, cellVal) => {
    const { attendanceId } = getCellInfo(cellVal);
    if (!attendanceId) return;

    const dayStr = String(day).padStart(2, '0');
    const jalaliDate = `${jYear}/${jMonthNumber}/${dayStr}`;

    setDetailContext({
      empId: record.id,
      empName: record.name,
      designation: record.designation,
      day,
      jalaliDate,
      attendanceId,
    });
    setDetailModalVisible(true);
    setDetailLoading(true);
    setDetailData(null);
    setDetailPermissions(null);

    try {
      const res = await attendanceApi.getById(attendanceId);
      setDetailData(res?.data?.attendance || res?.attendance || res?.data || null);
      setDetailPermissions(res?.permissions || null);
    } catch (error) {
      console.error(error);
      message.error('خطا در دریافت اطلاعات حضور');
      setDetailModalVisible(false);
      setDetailContext(null);
      setDetailPermissions(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditFromDetail = () => {
    if (!detailContext || !detailData) return;
    setDetailModalVisible(false);
    openAdjustModal(detailContext, detailData);
  };

  const isOwnDetail =
    Boolean(currentUser?.id) && String(detailContext?.empId) === String(currentUser.id);

  const handleRequestRegularization = () => {
    if (!detailContext || !detailData) return;
    const toHm = (v) => {
      if (!v) return null;
      if (typeof v === 'string' && /^\d{1,2}:\d{2}/.test(v)) return v.slice(0, 5);
      return moment(v).isValid() ? moment(v).format('HH:mm') : String(v).slice(0, 5);
    };
    const recordedIn = toHm(
      detailData.clock_in_time_formatted || detailData.start_time || detailData.clock_in_time,
    );
    setDetailModalVisible(false);
    navigate('/hr/attendance-regularizations', {
      state: {
        openCreate: true,
        tab: 'my_requests',
        preset: {
          attendance_id: detailContext.attendanceId || detailData.id || null,
          jalali_date: detailContext.jalaliDate,
          recorded_clock_in_time: recordedIn,
          type: 'clock_in',
        },
      },
    });
  };

  const handleDeleteAttendance = async () => {
    if (!detailContext?.attendanceId) return;
    setDeleteLoading(true);
    try {
      await attendanceApi.delete(detailContext.attendanceId);
      message.success('رکورد حضور حذف شد');
      setDetailModalVisible(false);
      setDetailContext(null);
      setDetailData(null);
      setDetailPermissions(null);
      onRefresh?.();
    } catch (error) {
      console.error(error);
      message.error(error?.message || 'خطا در حذف حضور');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ---- کلیک روی سلول ----
  const handleCellClick = (record, day, cellVal) => {
    const { visualKey } = getCellInfo(cellVal);
    // مأموریت / مرخصی / تعطیل — ثبت حضور دستی باز نشود
    if (['mission', 'leave', 'half_day_leave', 'holiday', 'day_off'].includes(visualKey)) {
      return;
    }
    if (hasRecordedAttendance(cellVal)) {
      handleViewAttendance(record, day, cellVal);
      return;
    }
    openMarkModal(record, day);
  };

  // ---- ثبت حضور ----
  const handleMarkSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!markModalData) return;

      let clockInTime = null;
      let clockOutTime = null;
      if (values.entryTime) {
        clockInTime = dayjs.isDayjs(values.entryTime)
          ? values.entryTime.format('HH:mm')
          : dayjs(values.entryTime).format('HH:mm');
      }
      if (values.exitTime) {
        clockOutTime = dayjs.isDayjs(values.exitTime)
          ? values.exitTime.format('HH:mm')
          : dayjs(values.exitTime).format('HH:mm');
      }

      const payload = {
        type: 'bulkMark',
        mark_attendance_by: 'date',
        user_id: [markModalData.empId],
        multi_date: `${markModalData.jalaliDate} - ${markModalData.jalaliDate}`,
        year: null,
        month: null,
        half_day: values.halfDay || 'no',
        half_day_duration: values.halfDayType || 'first_half',
        clock_in_time: clockInTime,
        clock_out_time: clockOutTime,
        late: values.delay || 'no',
        overwrite_attendance: 'no',
        working_from: null,
        work_from_type: values.entryWorkFrom || 'office',
        location: values.entryLocation ? Number(values.entryLocation) : null,
        clock_out_time_working_from: null,
        clock_out_time_work_from_type: values.exitWorkFrom || 'office',
        clock_out_time_location_id: values.exitLocation ? Number(values.exitLocation) : null,
      };

      setMarkLoading(true);
      await attendanceApi.bulkMark(payload);
      message.success('حضور با موفقیت ثبت شد');
      setMarkModalVisible(false);
      setMarkModalData(null);
      onRefresh?.();
    } catch (error) {
      if (error instanceof ValidationError || (error.error && error.error.details)) {
        show422Error(error, form);
      } else if (!error.errorFields) {
        message.error(error?.message || 'خطا در ثبت حضور');
      }
    } finally {
      setMarkLoading(false);
    }
  };

  // ---- رندر سلول‌ها ----
  const renderStatusCell = (val, record, day) => {
    const { isAdjusted, isLate, visualKey } = getCellInfo(val);
    const palette = getAttendancePalette(visualKey);
    const recorded = hasRecordedAttendance(val);
    const icon = (visualKey && statusIcons[visualKey]) || (palette ? statusIcons.present : <EditOutlined />);
    const missionDest =
      typeof val === 'object'
        ? val.destination || val.mission?.destination || null
        : null;
    const missionTrip =
      typeof val === 'object'
        ? val.trip_type || val.mission?.trip_type || null
        : null;

    return (
      <Tooltip
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            {visualKey === 'mission'
              ? `مأموریت${missionTrip === 'outside_city' ? ' برون‌شهری' : missionTrip === 'inside_city' ? ' درون‌شهری' : ''}${missionDest ? ` — ${missionDest}` : ''}`
              : palette?.title || palette?.text || 'ثبت نشده'}
            {isLate ? ' · تأخیر' : ''}
            {isAdjusted ? ' · اصلاح‌شده' : ''}
            {visualKey === 'mission'
              ? ''
              : recorded
                ? ' — کلیک برای مشاهده'
                : ' — کلیک برای ثبت حضور'}
          </span>
        }
      >
        <div
          onClick={() => handleCellClick(record, day, val)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 6,
            backgroundColor: palette ? palette.bg : '#f5f5f5',
            color: palette ? palette.color : '#d9d9d9',
            fontSize: 14,
            cursor: 'pointer',
            transition: 'all 0.2s',
            border: `1px solid ${palette?.border || 'transparent'}`,
            position: 'relative',
            boxShadow: isLate ? 'inset 0 0 0 1px rgba(245, 158, 11, 0.35)' : undefined,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          {icon}
          {isLate ? (
            <span
              style={{
                position: 'absolute',
                top: -3,
                right: -3,
                minWidth: 14,
                height: 14,
                borderRadius: 7,
                background: '#f59e0b',
                color: '#fff',
                fontSize: 8,
                fontWeight: 700,
                lineHeight: '14px',
                textAlign: 'center',
                border: '1px solid #fff',
                fontFamily: 'CustomFont',
              }}
              title="تأخیر"
            >
              ت
            </span>
          ) : null}
          {isAdjusted ? (
            <span
              style={{
                position: 'absolute',
                top: -2,
                left: -2,
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#3b82f6',
                border: '1px solid #fff',
              }}
            />
          ) : null}
        </div>
      </Tooltip>
    );
  };

  // ---- تبدیل داده‌ها ----
  const tableData = useMemo(() => {
    return filteredEmployees
      .map((emp) => {
        const userId = resolveUserId(emp);
        const empAttendance = rawAttendance[String(emp.id)]
          || rawAttendance[String(userId)]
          || {};
        const row = {
          id: emp.id,
          userId,
          name: emp.name,
          designation:
            emp.employee_detail?.designation?.name ||
            emp.designation?.name ||
            emp.designation_name ||
            '-',
          avatar: emp.image || emp.image_url || emp.avatar,
          summary:
            summaryByUserId[String(userId)]
            || summaryByUserId[String(emp.id)]
            || extractAttendanceSummary(emp.summary)
            || extractAttendanceSummary(emp.attendance_summary)
            || null,
        };

        Object.entries(empAttendance).forEach(([date, info]) => {
          const jDay = moment(date).jDate();
          row[`day${jDay}`] = info;
        });

        return row;
      });
  }, [filteredEmployees, rawAttendance, summaryByUserId]);

  const pageSize = filters.pageSize || 30;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage((prev) => (prev === 1 ? prev : 1));
  }, [jYear, jMonthNumber, department, designation, employee, pageSize]);

  const pagedTableData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return tableData.slice(start, start + pageSize);
  }, [tableData, currentPage, pageSize]);

  /** هر کارمند = یک ردیف خلاصه بالای ردیف روزها */
  const flatTableData = useMemo(() => {
    const rows = [];
    pagedTableData.forEach((emp) => {
      if (emp.summary || (summariesLoading && !emp.summary)) {
        rows.push({
          id: `summary-${emp.id}`,
          isSummaryRow: true,
          summary: emp.summary,
          name: emp.name,
          employeeId: emp.id,
          summaryLoading: summariesLoading && !emp.summary,
        });
      }
      rows.push({ ...emp, isSummaryRow: false });
    });
    return rows;
  }, [pagedTableData, summariesLoading]);

  const getWeekdayForDay = (day) => {
    const dayStr = String(day).padStart(2, '0');
    const jalaliDate = `${jYear}/${jMonthNumber}/${dayStr}`;
    return getWeekdayName(jalaliDate);
  };

  // ---- ستون‌های روز ----
  const dayColumns = useMemo(() => {
    const cols = [];
    for (let day = 1; day <= daysInMonth; day += 1) {
      const d = day;
      cols.push({
        title: (
          <div className="text-center" style={{ fontFamily: 'CustomFont', lineHeight: 1.2 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>{d}</div>
            <div style={{ fontSize: 10, color: '#8c8c8c' }}>{getWeekdayForDay(d)}</div>
          </div>
        ),
        dataIndex: `day${d}`,
        key: `day${d}`,
        width: 40,
        align: 'center',
        onCell: (record) => (record.isSummaryRow ? { colSpan: 0 } : {}),
        render: (val, record) => {
          if (record.isSummaryRow) return null;
          return renderStatusCell(val, record, d);
        },
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysInMonth, jYear, jMonthNumber]);

  const totalColSpan = 1 + daysInMonth;

  const columns = [
    {
      title: 'کارمند',
      key: 'employee',
      fixed: flatTableData.some((r) => r.isSummaryRow) ? undefined : 'right',
      width: 200,
      onCell: (record) => (record.isSummaryRow ? { colSpan: totalColSpan } : {}),
      render: (_, record) => {
        if (record.isSummaryRow) {
          return (
            <div className="py-1.5 px-2 bg-slate-50 dark:bg-gray-900/40 rounded-md border border-slate-100 dark:border-gray-700">
              <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-0.5" style={{ fontFamily: 'CustomFont' }}>
                خلاصه ماهانه — {record.name}
              </div>
              <AttendanceSummaryStrip
                summary={record.summary}
                loading={record.summaryLoading}
              />
            </div>
          );
        }
        return (
          <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <Avatar src={record.avatar} icon={<UserOutlined />} size="small" />
            <div>
              <div className="font-medium text-gray-800 dark:text-gray-100" style={{ fontSize: 13 }}>
                {record.name}
              </div>
              {record.designation && record.designation !== '-' && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {record.designation}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    ...dayColumns,
  ];

  // ---- ریست فیلترها ----
  const handleResetFilters = () => {
    const selfId = defaultEmployeeId || 'all';
    onFiltersChange?.({
      year: moment().format('jYYYY'),
      month: moment().format('jM').padStart(2, '0'),
      monthName: (() => {
        const m = moment().format('jM');
        const map = {
          '1': 'فروردین', '2': 'اردیبهشت', '3': 'خرداد', '4': 'تیر', '5': 'مرداد', '6': 'شهریور',
          '7': 'مهر', '8': 'آبان', '9': 'آذر', '10': 'دی', '11': 'بهمن', '12': 'اسفند',
        };
        return map[m] || 'آذر';
      })(),
      department: 'all',
      designation: 'all',
      employee: selfId,
      userId: selfId,
      _employeeTouched: false,
    });
  };

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      {/* فیلترهای پیشرفته */}
      <div className="mb-6">
        <div className="mb-4 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
          <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>سال</label>
                <Select value={jYear} onChange={(v) => onFiltersChange?.({ year: v })} style={{ width: '100%', fontFamily: 'CustomFont' }}>
                  <Option value="1405">۱۴۰۵</Option>
                  <Option value="1404">۱۴۰۴</Option>
                  <Option value="1403">۱۴۰۳</Option>
                  <Option value="1402">۱۴۰۲</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>ماه</label>
                <Select value={jMonthName} onChange={(v) => onFiltersChange?.({ monthName: v })} style={{ width: '100%', fontFamily: 'CustomFont' }}>
                  <Option value="فروردین">فروردین</Option>
                  <Option value="اردیبهشت">اردیبهشت</Option>
                  <Option value="خرداد">خرداد</Option>
                  <Option value="تیر">تیر</Option>
                  <Option value="مرداد">مرداد</Option>
                  <Option value="شهریور">شهریور</Option>
                  <Option value="مهر">مهر</Option>
                  <Option value="آبان">آبان</Option>
                  <Option value="آذر">آذر</Option>
                  <Option value="دی">دی</Option>
                  <Option value="بهمن">بهمن</Option>
                  <Option value="اسفند">اسفند</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>کارمند</label>
                <Select value={employee === 'all' || employees.some((e) => String(e.id) === String(employee)) ? employee : undefined} onChange={(v) => onFiltersChange?.({ employee: v, userId: v })} style={{ width: '100%', fontFamily: 'CustomFont' }} placeholder="همه کارمندان" showSearch optionFilterProp="children" allowClear>
                  <Option value="all">همه</Option>
                  {employees.map((e) => <Option key={e.id} value={String(e.id)}>{e.name}</Option>)}
                </Select>
              </Col>
          </Row>
        </div>

        <button
          onClick={() => setFilterExpanded(!filterExpanded)}
          className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <FilterOutlined className="text-gray-600 dark:text-gray-400" />
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'CustomFont' }}>
              فیلترهای بیشتر
            </span>
          </div>
          {filterExpanded ? <span className="text-gray-400">▲</span> : <span className="text-gray-400">▼</span>}
        </button>

        {filterExpanded && (
          <div className="mt-4 p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={6}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>دپارتمان</label>
                <Select value={department} onChange={(v) => onFiltersChange?.({ department: v })} style={{ width: '100%', fontFamily: 'CustomFont' }} placeholder="همه دپارتمان‌ها" showSearch optionFilterProp="children" allowClear>
                  <Option value="all">همه</Option>
                  {departments.map((d) => <Option key={d.id} value={String(d.id)}>{d.team_name || d.name}</Option>)}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8} lg={6}>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5" style={{ fontFamily: 'CustomFont' }}>عنوان شغلی</label>
                <Select value={designation} onChange={(v) => onFiltersChange?.({ designation: v })} style={{ width: '100%', fontFamily: 'CustomFont' }} placeholder="همه سمت‌ها" showSearch optionFilterProp="children" allowClear>
                  <Option value="all">همه</Option>
                  {designations.map((d) => <Option key={d.id} value={String(d.id)}>{d.name}</Option>)}
                </Select>
              </Col>
            </Row>
            <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-gray-100 dark:border-gray-700">
              <Button icon={<ReloadOutlined />} onClick={handleResetFilters} style={{ fontFamily: 'CustomFont' }}>
                ریست فیلترها
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* راهنمای نمادها */}
      <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
        <span className="font-semibold text-gray-700 dark:text-gray-200">راهنما:</span>
        {[
          { key: 'present', label: 'حضور به‌موقع' },
          { key: 'late', label: 'تأخیر' },
          { key: 'absent', label: 'غیبت' },
          { key: 'half_day', label: 'نیم‌روز' },
          { key: 'leave', label: 'مرخصی' },
          { key: 'mission', label: 'مأموریت' },
          { key: 'holiday', label: 'تعطیل / استراحت' },
        ].map((item) => {
          const palette = getAttendancePalette(item.key);
          const icon = statusIcons[item.key] || statusIcons.present;
          return (
            <span key={item.key} className="inline-flex items-center gap-1.5">
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 22,
                  height: 22,
                  borderRadius: 5,
                  backgroundColor: palette?.bg,
                  color: palette?.color,
                  border: `1px solid ${palette?.border || 'transparent'}`,
                  fontSize: 12,
                }}
              >
                {icon}
              </span>
              <span style={{ fontSize: 12 }}>{item.label}</span>
            </span>
          );
        })}
      </div>

      {/* جدول */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        {loading ? (
          <div className="p-6"><TableSkeleton rows={5} columns={6} /></div>
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={flatTableData}
              rowKey="id"
              pagination={false}
              scroll={{ x: 'max-content' }}
              loading={false}
              rowClassName={(record) => (record.isSummaryRow ? 'attendance-summary-row' : '')}
              locale={{
                emptyText: (
                  <Empty
                    description={<span style={{ fontFamily: 'CustomFont' }}>هیچ رکوردی یافت نشد</span>}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
              style={{ fontFamily: 'CustomFont' }}
              size="small"
            />
            {tableData.length > 0 && (
              <div className="flex justify-end p-3 border-t border-gray-100 dark:border-gray-700">
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={tableData.length}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '30', '50', '100']}
                  showTotal={(total) => (
                    <span style={{ fontFamily: 'CustomFont' }}>{`مجموع ${total} کارمند`}</span>
                  )}
                  onChange={(page, size) => {
                    setCurrentPage(page);
                    if (size !== pageSize) onFiltersChange?.({ pageSize: size });
                  }}
                  style={{ fontFamily: 'CustomFont' }}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* ===== مودال ثبت حضور ===== */}
      <Modal
        open={markModalVisible}
        onCancel={() => { setMarkModalVisible(false); setMarkModalData(null); }}
        title={
          <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <ClockCircleOutlined className="text-blue-500" />
            <span>ثبت حضور و غیاب</span>
          </div>
        }
        footer={null}
        centered
        width={520}
        destroyOnClose
      >
        {markModalData && (
          <div style={{ fontFamily: 'CustomFont' }}>
            {/* اطلاعات کارمند و تاریخ */}
            <div className="rounded-lg p-4 mb-5" style={{ backgroundColor: '#f0f5ff', border: '1px solid #adc6ff' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                  <div>
                    <div className="font-bold" style={{ color: '#0050b3' }}>{markModalData.empName}</div>
                    {markModalData.designation && markModalData.designation !== '-' && (
                      <div className="text-xs" style={{ color: '#597ef7' }}>{markModalData.designation}</div>
                    )}
                  </div>
                </div>
                <div className="text-left">
                  <div className="font-bold" style={{ color: '#0050b3' }}>{markModalData.jalaliDate}</div>
                  <div className="text-xs" style={{ color: '#597ef7' }}>{getWeekdayName(markModalData.jalaliDate)}</div>
                </div>
              </div>
            </div>

            <Form form={form} layout="vertical" style={{ fontFamily: 'CustomFont' }}>
              {/* ورود */}
              <div className="mb-2 font-semibold text-gray-700 dark:text-gray-200" style={{ fontSize: 14 }}>ورود</div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Form.Item label={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>زمان ورود</span>} name="entryTime" className="mb-0">
                  <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="08:00" />
                </Form.Item>
                <Form.Item label={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>کار از</span>} name="entryWorkFrom" className="mb-0">
                  <Select style={{ fontFamily: 'CustomFont' }}>
                    <Option value="office">دفتر</Option>
                    <Option value="home">خانه</Option>
                    <Option value="other">سایر</Option>
                  </Select>
                </Form.Item>
                <Form.Item label={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>موقعیت</span>} name="entryLocation" className="mb-0">
                  <Select placeholder="انتخاب" style={{ fontFamily: 'CustomFont' }} allowClear>
                    {locations.map((loc) => (
                      <Option key={loc.id} value={String(loc.id)}>{loc.location || loc.address || loc.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              {/* خروج */}
              <div className="mb-2 font-semibold text-gray-700 dark:text-gray-200" style={{ fontSize: 14 }}>خروج</div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Form.Item label={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>زمان خروج</span>} name="exitTime" className="mb-0">
                  <TimePicker format="HH:mm" style={{ width: '100%' }} placeholder="16:00" />
                </Form.Item>
                <Form.Item label={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>کار از</span>} name="exitWorkFrom" className="mb-0">
                  <Select style={{ fontFamily: 'CustomFont' }}>
                    <Option value="office">دفتر</Option>
                    <Option value="home">خانه</Option>
                    <Option value="other">سایر</Option>
                  </Select>
                </Form.Item>
                <Form.Item label={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>موقعیت</span>} name="exitLocation" className="mb-0">
                  <Select placeholder="انتخاب" style={{ fontFamily: 'CustomFont' }} allowClear>
                    {locations.map((loc) => (
                      <Option key={loc.id} value={String(loc.id)}>{loc.location || loc.address || loc.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              {/* تاخیر و نیم روز */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Form.Item label={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>تاخیر</span>} name="delay" className="mb-0">
                  <Radio.Group style={{ fontFamily: 'CustomFont' }}>
                    <Radio value="yes">بله</Radio>
                    <Radio value="no">خیر</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item label={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>نیم روز</span>} name="halfDay" className="mb-0">
                  <Radio.Group style={{ fontFamily: 'CustomFont' }}>
                    <Radio value="yes">بله</Radio>
                    <Radio value="no">خیر</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item label={<span style={{ fontFamily: 'CustomFont', fontSize: 12 }}>نوع نیم روز</span>} name="halfDayType" className="mb-0">
                  <Select style={{ fontFamily: 'CustomFont' }}>
                    <Option value="first_half">نیمه اول</Option>
                    <Option value="second_half">نیمه دوم</Option>
                  </Select>
                </Form.Item>
              </div>

              <Alert
                type="info"
                showIcon
                className="mb-4 custom-font text-[11px]"
                message="برای اصلاح ساعت قبلی، از جزئیات حضور و گزینه «تعدیل» استفاده کنید."
              />

              {/* دکمه‌ها */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <Button onClick={() => { setMarkModalVisible(false); setMarkModalData(null); }} style={{ fontFamily: 'CustomFont' }}>
                  انصراف
                </Button>
                <Button
                  type="primary"
                  onClick={handleMarkSubmit}
                  loading={markLoading}
                  className="bg-green-600 hover:bg-green-700 border-green-600"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ثبت حضور
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Modal>

      <AttendanceAdjustModal
        open={adjustModalOpen}
        onClose={() => {
          setAdjustModalOpen(false);
          setAdjustTarget(null);
        }}
        attendanceId={adjustTarget?.attendanceId}
        empName={adjustTarget?.empName}
        jalaliDate={adjustTarget?.jalaliDate}
        initialClockIn={adjustTarget?.clockIn}
        initialClockOut={adjustTarget?.clockOut}
        onSuccess={() => onRefresh?.()}
      />

      {/* ===== مودال مشاهده حضور ثبت‌شده ===== */}
      <Modal
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setDetailContext(null);
          setDetailData(null);
          setDetailPermissions(null);
        }}
        title={
          <div className="flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
            <EyeOutlined className="text-blue-500" />
            <span>جزئیات حضور</span>
          </div>
        }
        footer={
          detailData ? (
            <div className="flex items-center justify-between w-full gap-2">
              {canDeleteDetail ? (
                <Popconfirm
                  title="حذف رکورد حضور؟"
                  description="در صورت امکان به‌جای حذف از «تعدیل» استفاده کنید."
                  onConfirm={handleDeleteAttendance}
                  okText="بله، حذف کن"
                  cancelText="لغو"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={deleteLoading}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    حذف
                  </Button>
                </Popconfirm>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setDetailModalVisible(false);
                    setDetailContext(null);
                    setDetailData(null);
                    setDetailPermissions(null);
                  }}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  بستن
                </Button>
                {isOwnDetail ? (
                  <Button
                    icon={<FormOutlined />}
                    onClick={handleRequestRegularization}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    درخواست اصلاح تردد
                  </Button>
                ) : null}
                {canEditDetail ? (
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={handleEditFromDetail}
                    className="bg-amber-600 hover:bg-amber-700 border-amber-600"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    تعدیل
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null
        }
        centered
        width={520}
        destroyOnClose
      >
        {detailLoading ? (
          <div className="flex justify-center py-12">
            <Spin size="large" />
          </div>
        ) : detailData && detailContext ? (
          <div style={{ fontFamily: 'CustomFont' }}>
            <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: '#f0f5ff', border: '1px solid #adc6ff' }}>
              <div className="font-bold text-blue-800">{detailContext.empName}</div>
              <div className="text-sm text-blue-600 mt-1">{detailContext.jalaliDate}</div>
            </div>
            <Descriptions
              bordered
              column={1}
              size="middle"
              labelStyle={{ fontFamily: 'CustomFont', fontWeight: 'bold', width: 130 }}
              contentStyle={{ fontFamily: 'CustomFont' }}
            >
              <Descriptions.Item label="تاریخ حضور">
                {detailData.attendance_date
                  ? moment(detailData.attendance_date).format('jYYYY/jMM/jDD HH:mm')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="زمان ورود">
                <AttendanceAdjustedTime
                  time={
                    detailData.clock_in_time_formatted ||
                    detailData.start_time ||
                    detailData.clock_in_time
                  }
                  isAdjusted={Boolean(detailData.is_adjusted)}
                  attendanceId={detailContext.attendanceId}
                  field="clock_in"
                  originalFormatted={
                    detailData.original_clock_in_time_formatted ||
                    detailData.original_clock_in_time
                  }
                  adjustments={detailData.adjustments}
                />
              </Descriptions.Item>
              <Descriptions.Item label="زمان خروج">
                <AttendanceAdjustedTime
                  time={
                    detailData.clock_out_time_formatted ||
                    detailData.end_time ||
                    detailData.clock_out_time
                  }
                  isAdjusted={Boolean(detailData.is_adjusted)}
                  attendanceId={detailContext.attendanceId}
                  field="clock_out"
                  originalFormatted={
                    detailData.original_clock_out_time_formatted ||
                    detailData.original_clock_out_time
                  }
                  adjustments={detailData.adjustments}
                />
              </Descriptions.Item>
              {detailData.is_adjusted ? (
                <Descriptions.Item label="وضعیت">
                  <Tag color="gold" icon={<AuditOutlined />} className="custom-font m-0">
                    اصلاح‌شده
                  </Tag>
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label="مجموع کار">
                {detailData.total_time_human || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="خروج ثبت نشده">
                {detailData.not_clocked_out ? 'بله' : 'خیر'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default AttendanceListView;
